import React, { forwardRef } from 'react';
import Box from '@mui/material/Box';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

/**
 * Affordance "menu secondaire" des cartes de controle (progression, metronome,
 * voicing d'accord).
 *
 * Un seul composant pour les trois, parce que trois icones stylees a la main
 * avaient fini par diverger : deux points decoratifs en bas sur certaines
 * cartes, un vrai bouton en haut a droite sur une autre, teintes differentes.
 * Le meme signe doit vouloir dire la meme chose partout.
 *
 * Le geste long est un accelerateur, jamais le seul chemin : il n'est pas
 * decouvrable, et Apple demande que la fonction d'un menu contextuel reste
 * accessible autrement. Ces points sont donc toujours cliquables, sur mobile
 * comme sur desktop, et toujours visibles.
 *
 * Les points portent leur propre pastille, parce que le probleme n'etait pas
 * seulement de rater la cible : rien ne disait que cette zone etait un bouton
 * distinct de la carte. Un fond rend la frontiere visible avant qu'on touche.
 *
 * La cible fait 32x26 et non les 44pt d'Apple : sur une carte de 62x94, une
 * zone de 44 de haut recouvrait l'icone primaire, dont le centre tombait pile
 * sur son bord. On ouvrait le menu en voulant lancer la lecture. Entre une
 * cible un peu courte dans un angle et un bouton qui vole l'action primaire,
 * la prevention d'erreur passe devant la regle de taille, d'autant que l'appui
 * long reste un second chemin.
 *
 * Le contenu des cartes est decale vers le bas (CARD_CONTENT_SX dans
 * NoteGrid) pour degager ces 26px : les deux reglages vont ensemble.
 *
 * La carte Play de la progression est la seule blanche : un signe blanc
 * translucide y disparaissait, le menu n'existait plus que pour qui survolait
 * l'angle a la souris. D'ou `tone="light"`, les memes valeurs en noir, le
 * signe un cran plus appuye : sur du blanc pur, 0,55 rendait des points plus
 * pales que sur les cartes sombres a valeur egale.
 */
const TONES = {
  dark: {
    // 0.55 sur le fond sombre des cartes tient le 3:1 exige par WCAG pour un
    // composant d'interface, tout en restant sous le contenu principal.
    dot: 'rgba(255, 255, 255, 0.09)',
    glyph: 'rgba(255, 255, 255, 0.55)',
    dotHover: 'rgba(255, 255, 255, 0.2)',
    glyphHover: 'rgba(255, 255, 255, 1)'
  },
  light: {
    dot: 'rgba(0, 0, 0, 0.09)',
    glyph: 'rgba(0, 0, 0, 0.75)',
    dotHover: 'rgba(0, 0, 0, 0.2)',
    glyphHover: 'rgba(0, 0, 0, 1)'
  }
};
// forwardRef : le libelle de survol du desktop est un Tooltip MUI, qui a besoin
// d'accrocher une ref sur son enfant.
const CardOverflowButton = forwardRef(({ onOpen, label, tone = 'dark', ...props }, ref) => {
  const t = TONES[tone] || TONES.dark;
  return (
  <Box
    ref={ref}
    {...props}
    role="button"
    aria-label={label}
    onClick={(e) => { e.stopPropagation(); onOpen(); }}
    onMouseDown={(e) => e.stopPropagation()}
    onTouchStart={(e) => e.stopPropagation()}
    sx={{
      position: 'absolute',
      top: 0,
      right: 0,
      width: 32,
      height: 26,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
      paddingTop: '3px',
      paddingRight: '3px',
      cursor: 'pointer',
      zIndex: 2,
      'body.compact-mode &': { width: 26, height: 22 },
      '&:hover .card-overflow-dot, &:active .card-overflow-dot': {
        backgroundColor: t.dotHover,
        color: t.glyphHover
      }
    }}
  >
    <Box
      className="card-overflow-dot"
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: t.dot,
        color: t.glyph,
        transition: 'background-color 150ms ease, color 150ms ease',
        'body.compact-mode &': { width: 18, height: 18 }
      }}
    >
      <MoreHorizIcon sx={{ fontSize: '0.85rem' }} />
    </Box>
  </Box>
  );
});

export default CardOverflowButton;
